import {
  companyEnrichmentStore,
  toCompanyEnrichmentView,
  type CompanyEnrichmentView,
} from "@/components/company/shared/company-enrichment-store";
import { useApiStream } from "@/hooks/useApi";
import { useCallback, useSyncExternalStore } from "react";

type UseCompanyEnrichmentResult = CompanyEnrichmentView & {
  start: () => void;
};

/**
 * Subscribes to the company's run in the shared company page store.
 *
 * The store is module-level so multiple components (and even the same panel
 * after a remount) observe the same in-flight stream. Calling start() when a
 * stream is already active for this company is a no-op.
 */
export function useCompanyEnrichment(
  companyId: string | null,
): UseCompanyEnrichmentResult {
  const { get, teamId } = useApiStream();
  const state = useSyncExternalStore(companyEnrichmentStore.subscribe, () =>
    companyId ? companyEnrichmentStore.getState(teamId, companyId) : undefined,
  );

  const start = useCallback(() => {
    if (!companyId) return;
    void companyEnrichmentStore.start(teamId, companyId, get);
  }, [teamId, companyId, get]);

  const isManualRun =
    !!companyId && companyEnrichmentStore.isManualRun(teamId, companyId);

  return { ...toCompanyEnrichmentView(state, isManualRun), start };
}
